<script setup lang="ts">
  const { t, tmResolved } = useResolvedI18n()
  const localePath = useLocalePath()

  useHead({ title: () => t('admission.title') })

  const majors = computed(() =>
    tmResolved<
      Array<{
        name: string
        duration: string
        degree: string
        credits: string
      }>
    >('admission.brochure.majors'),
  )
  const features = computed(() => tmResolved<Array<{ label: string; desc: string }>>('admission.brochure.features'))
  const adJobs = computed(() => tmResolved<string[]>('admission.career.adJobs'))
  const icJobs = computed(() => tmResolved<string[]>('admission.career.icJobs'))
  const faqItems = computed(() => tmResolved<Array<{ q: string; a: string }>>('admission.faq.items'))
</script>

<template>
  <section
    class="page-banner"
    style="
      background-image: url('https://www.sanyau.edu.cn/_upload/article/images/f4/cb/2bd41a6e4f7782b507af0c4047ef/0381d126-fbc0-430e-9a2e-faf88080b1ff.jpg');
    "
  >
    <h2>{{ t('admission.banner') }}</h2>
    <div class="breadcrumb">
      <NuxtLink :to="localePath('/')">{{ t('common.home') }}</NuxtLink>
      &gt; {{ t('admission.breadcrumb') }}
    </div>
  </section>

  <section class="content-section">
    <div class="container">
      <div class="content-wrapper">
        <div class="content-main">
          <div class="content-card" id="brochure">
            <h3>{{ t('admission.brochure.title') }}</h3>

            <div class="highlight-box">
              <h3>{{ t('admission.brochure.highlightTitle') }}</h3>
              <p>{{ t('admission.brochure.highlightBody') }}</p>
            </div>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('admission.brochure.majorsTitle') }}
            </h4>
            <div class="table-scroll">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>{{ t('admission.brochure.colMajor') }}</th>
                    <th>{{ t('admission.brochure.colDuration') }}</th>
                    <th>{{ t('admission.brochure.colDegree') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(major, i) in majors" :key="i">
                    <td>{{ major.name }}</td>
                    <td>{{ major.duration }}</td>
                    <td>{{ major.degree }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('admission.brochure.featuresTitle') }}
            </h4>
            <ul>
              <li v-for="(item, i) in features" :key="i">
                <strong>{{ item.label }}</strong>
                {{ item.desc }}
              </li>
            </ul>
          </div>

          <div class="content-card" id="guide">
            <h3>{{ t('admission.guide.title') }}</h3>

            <div class="highlight-box">
              <h4>{{ t('admission.guide.requirementsTitle') }}</h4>
              <p>{{ t('admission.guide.requirements') }}</p>
            </div>

            <div class="highlight-box">
              <h4>{{ t('admission.guide.admissionTitle') }}</h4>
              <p>{{ t('admission.guide.admission') }}</p>
            </div>

            <div class="highlight-box">
              <h4>{{ t('admission.guide.tuitionTitle') }}</h4>
              <p>{{ t('admission.guide.tuition') }}</p>
            </div>

            <div class="highlight-box">
              <h4>{{ t('admission.guide.aidTitle') }}</h4>
              <p>{{ t('admission.guide.aid') }}</p>
            </div>
          </div>

          <div class="content-card" id="career">
            <h3>{{ t('admission.career.title') }}</h3>
            <p>{{ t('admission.career.intro') }}</p>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('admission.career.adTitle') }}
            </h4>
            <ul>
              <li v-for="(job, i) in adJobs" :key="i">{{ job }}</li>
            </ul>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('admission.career.icTitle') }}
            </h4>
            <ul>
              <li v-for="(job, i) in icJobs" :key="i">{{ job }}</li>
            </ul>
          </div>
        </div>

        <aside class="content-sidebar">
          <div class="sidebar-nav">
            <h4>{{ t('admission.sidebar.navTitle') }}</h4>
            <ul>
              <li>
                <a href="#brochure">{{ t('admission.sidebar.navBrochure') }}</a>
              </li>
              <li>
                <a href="#guide">{{ t('admission.sidebar.navGuide') }}</a>
              </li>
              <li>
                <a href="#career">{{ t('admission.sidebar.navCareer') }}</a>
              </li>
              <li>
                <a href="#faq">{{ t('admission.sidebar.navFaq') }}</a>
              </li>
            </ul>
          </div>
          <div class="sidebar-nav">
            <h4>{{ t('admission.sidebar.consultTitle') }}</h4>
            <p style="font-size: 14px; color: var(--text-secondary); margin-bottom: 12px">
              {{ t('admission.sidebar.hotlineLabel') }}
              <br />
              <strong>{{ t('footer.phoneValue') }}</strong>
            </p>
            <p style="font-size: 14px; color: var(--text-secondary); margin-bottom: 12px">
              {{ t('admission.sidebar.emailLabel') }}
              <br />
              <strong>{{ t('footer.emailValue') }}</strong>
            </p>
            <p style="font-size: 14px; color: var(--text-secondary)">
              {{ t('admission.sidebar.siteLabel') }}
              <br />
              <a href="http://zhaosheng.sanyau.edu.cn/" target="_blank" rel="noopener" style="color: var(--theme-blue)">
                {{ t('admission.sidebar.site') }}
              </a>
            </p>
          </div>
        </aside>
      </div>
    </div>
  </section>
</template>
